import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonInput, IonLabel, IonItem, IonList, IonModal, IonContent } from "@ionic/react";
import { Helmet } from "react-helmet";

const GreaterLessThan: React.FC = () => {
    const [number1, setNumber1] = useState<number>(0);
    const [number2, setNumber2] = useState<number>(0);
    const [userInput, setUserInput] = useState<string>("");
    const [feedback, setFeedback] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Function to generate new question
    const generateQuestion = () => {
        setNumber1(Math.floor(Math.random() * 90) + 10); // Random two-digit number
        setNumber2(Math.floor(Math.random() * 90) + 10);
        setUserInput("");
        setFeedback(null);
    };

    // Check user answer
    const checkAnswer = (operator: string) => {
        let correctOperator;
        if (number1 > number2) {
            correctOperator = ">";
        } else if (number1 < number2) {
            correctOperator = "<";
        } else {
            correctOperator = "=";
        }

        if (operator === correctOperator) {
            setShowImage(true);
            setFeedback("Correct! Generating a new question...");
            setTimeout(() => {
                generateQuestion();
                setShowImage(false);
                setError(false);
            }, 5000); // Generate a new question after 2 seconds
        } else {
            setError(true);
        }
    };

    // Generate the first question on component mount
    useEffect(() => {
        generateQuestion();
        document.title = "Number Comparison Worksheets for UKG | Greater, Less & Equal";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Help your UKG child compare numbers confidently with free printable worksheets. Practice greater than, less than, and equal to concepts through fun activities." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/number-comparison" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className='contentPart additionWord pickColor matchImage match-container'>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Number Comparison</h1>

                                    <p>
                                        Comparing numbers is one of the most important skills children learn in UKG. It helps them understand number values, order, and sets the foundation for arithmetic. At Mama’s Guidance, we provide fun, engaging <strong>Number Comparison worksheets</strong> to make learning joyful and effective.
                                    </p>

                                    <h2>🔍 What is Number Comparison?</h2>
                                    <p>
                                        Number comparison means identifying whether one number is <strong>greater than</strong>, <strong>less than</strong>, or <strong>equal to</strong> another. UKG kids learn to use symbols like:
                                    </p>
                                    <ul>
                                        <li><strong>Greater Than ( &gt; )</strong></li>
                                        <li><strong>Less Than ( &lt; )</strong></li>
                                        <li><strong>Equal To ( = )</strong></li>
                                    </ul>
                                    <p><strong>Example:</strong> 6 &lt; 9, 10 &gt; 7, 5 = 5</p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify the larger or smaller number in a pair</li>
                                        <li>Use the correct comparison symbols (&gt;, &lt;, =)</li>
                                        <li>Develop number sense and logical reasoning</li>
                                        <li>Get ready for addition and subtraction concepts</li>
                                    </ul>

                                    <h2>📝 Fun Activities & Worksheets</h2>
                                    <ul>
                                        <li>Circle the greater or lesser number</li>
                                        <li>Match numbers with the correct comparison symbol</li>
                                        <li>Fill in the blanks with &gt;, &lt;, or =</li>
                                        <li>Count and compare groups of objects</li>
                                        <li>Use number lines for visual comparison</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/substraction"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Substraction</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>👩‍🏫 Tips to Teach Number Comparison</h2>
                                    <ul>
                                        <li>Use real objects (blocks, pencils, fruits) for hands-on comparison</li>
                                        <li>Explain the “crocodile mouth” method: the mouth always opens to the bigger number</li>
                                        <li>Practice daily: “Do you have more toys or fewer books?”</li>
                                        <li>Play card games or use dice to create quick compare-and-win challenges</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Number Comparison</h1>

                                <p>
                                    Comparing numbers is one of the most important skills children learn in UKG. It helps them understand number values, order, and sets the foundation for arithmetic. At Mama’s Guidance, we provide fun, engaging <strong>Number Comparison worksheets</strong> to make learning joyful and effective.
                                </p>

                                <h2>🔍 What is Number Comparison?</h2>
                                <p>
                                    Number comparison means identifying whether one number is <strong>greater than</strong>, <strong>less than</strong>, or <strong>equal to</strong> another. UKG kids learn to use symbols like:
                                </p>
                                <ul>
                                    <li><strong>Greater Than ( &gt; )</strong></li>
                                    <li><strong>Less Than ( &lt; )</strong></li>
                                    <li><strong>Equal To ( = )</strong></li>
                                </ul>
                                <p><strong>Example:</strong> 6 &lt; 9, 10 &gt; 7, 5 = 5</p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify the larger or smaller number in a pair</li>
                                    <li>Use the correct comparison symbols (&gt;, &lt;, =)</li>
                                    <li>Develop number sense and logical reasoning</li>
                                    <li>Get ready for addition and subtraction concepts</li>
                                </ul>

                                <h2>📝 Fun Activities & Worksheets</h2>
                                <ul>
                                    <li>Circle the greater or lesser number</li>
                                    <li>Match numbers with the correct comparison symbol</li>
                                    <li>Fill in the blanks with &gt;, &lt;, or =</li>
                                    <li>Count and compare groups of objects</li>
                                    <li>Use number lines for visual comparison</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/substraction"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Substraction</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>👩‍🏫 Tips to Teach Number Comparison</h2>
                                <ul>
                                    <li>Use real objects (blocks, pencils, fruits) for hands-on comparison</li>
                                    <li>Explain the “crocodile mouth” method: the mouth always opens to the bigger number</li>
                                    <li>Practice daily: “Do you have more toys or fewer books?”</li>
                                    <li>Play card games or use dice to create quick compare-and-win challenges</li>
                                </ul>
                            </article>
                            <div className="worksheet-container text-center" style={{ marginTop: '70px', fontSize: '40px' }}>
                                <div className="text-center" style={{ marginLeft: 0, display: 'flex', gap: '30px' }}>
                                    <div className="grid-box">    {number1} </div>
                                    <div className="grid-box" style={{ width: '50px' }}>
                                        ?
                                    </div>
                                    <div className="grid-box">    {number2}</div>
                                </div>

                                <div className=" mt-4 text-center" style={{ marginLeft: 0, display: 'flex', gap: '30px' }}>
                                    <div style={{ width: 'auto', padding: '20px', fontSize: '30px' }} className=" background-blue" onClick={() => checkAnswer(">")}>&gt;</div>
                                    <div style={{ width: 'auto', padding: '20px', fontSize: '30px' }} className=" background-blue" onClick={() => checkAnswer("<")}>&lt;</div>
                                    <div style={{ width: 'auto', padding: '20px', fontSize: '30px' }} className=" background-blue" onClick={() => checkAnswer("=")}>=</div>
                                </div>
                            </div>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default GreaterLessThan;
